import { AlertTriangle, Trash2, X } from 'lucide-react'

interface ConfirmModalProps {
  isOpen: boolean
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'warning' | 'default'
  onConfirm: () => void
  onCancel: () => void
  isLoading?: boolean
}

export default function ConfirmModal({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  onConfirm,
  onCancel,
  isLoading = false
}: ConfirmModalProps): JSX.Element | null {
  if (!isOpen) return null

  const getIcon = () => {
    switch (variant) {
      case 'danger':
        return <Trash2 size={24} />
      case 'warning':
        return <AlertTriangle size={24} />
      default:
        return null
    }
  }

  const getIconClass = () => {
    switch (variant) {
      case 'danger':
        return 'confirm-modal__icon--danger'
      case 'warning':
        return 'confirm-modal__icon--warning'
      default:
        return ''
    }
  }

  const getConfirmButtonClass = () => {
    switch (variant) {
      case 'danger':
        return 'confirm-modal__btn--danger'
      default:
        return 'primary-button'
    }
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
        {/* The icon is the only content, so without a label this button reaches
            assistive tech unnamed. Deliberately NOT `cancelText` or "Close":
            it would then share a name with the dialog's own Cancel button, and
            with the Close button of whatever modal opened this one — ambiguous
            to a screen reader reading the button list, and to any query by
            accessible name. */}
        <button className="confirm-modal__close" onClick={onCancel} aria-label="Dismiss">
          <X size={20} />
        </button>

        {getIcon() && (
          <div className={`confirm-modal__icon ${getIconClass()}`}>
            {getIcon()}
          </div>
        )}

        <h3>{title}</h3>
        <p>{message}</p>

        <div className="modal-actions">
          <button
            className={getConfirmButtonClass()}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : confirmText}
          </button>
          <button onClick={onCancel} disabled={isLoading}>
            {cancelText}
          </button>
        </div>
      </div>
    </div>
  )
}
